import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import archibestEvent from "@/assets/archibest-event.webp";
import paAcademyEvent from "@/assets/pa-academy-event.webp";
import narvaBackground from "@/assets/narva-background.webp";
import agenticStudioFeatured from "@/assets/agentic/astra-terrace-salon.webp";
import highEndHero from "@/assets/highend/hero-interior-card.webp";
import studioToScreenCourse from "@/assets/courses/studio-to-screen-poster-v2.png";

const upcomingEvents = [
  {
    id: 9,
    category: "Masterclass",
    title: "Agentic Studio Masterclass",
    date: "2 Oct",
    time: "3 PM (BST)",
    speaker: "Amir Hossein Noori",
    image: agenticStudioFeatured,
    link: "/agentic-studio",
    ctaLabel: "See details",
  },
  {
    id: 2,
    category: "Online Workshop",
    title: "PA Academy: The AI-Native Architect",
    date: "28-29 Mar",
    time: "2-6 PM (GMT)",
    speaker: "Amir Hossein Noori",
    image: paAcademyEvent,
    link: "https://paacademy.com/course/the-ai-native-architect",
    ctaLabel: "Register",
  },
  {
    id: 3,
    category: "Online Workshop",
    title: "Archibest: The Generative Studio",
    date: "16-17 Apr",
    time: "3-7 PM (GMT)",
    speaker: "Amir Hossein Noori",
    image: archibestEvent,
    link: "https://archibestacademy.net/courses/the-generative-studio",
    ctaLabel: "Register",
  },
];

const pastEvents = [
  {
    id: 201,
    category: "Live Demo",
    title: "Narva Agentic Suite: Demo & Q&A",
    date: "15 Apr",
    time: "3 PM (BST)",
    speaker: "Amir & Mo",
    image: narvaBackground,
    link: "/narva-demo",
    ctaLabel: "See details",
  },
  {
    id: 202,
    category: "Masterclass",
    title: "The High-End Studio",
    date: "4 Sept",
    time: "3 PM (BST)",
    speaker: "Amir Hossein Noori",
    image: highEndHero,
    link: "/high-end-studio",
    ctaLabel: "See details",
  },
  {
    id: 203,
    category: "Masterclass",
    title: "Studio to Screen Masterclass",
    date: "1 May",
    time: "3 PM (BST)",
    speaker: "Amir Hossein Noori",
    image: studioToScreenCourse,
    link: "/studio-to-screen",
    ctaLabel: "See details",
  },
];

const allEvents = [...upcomingEvents, ...pastEvents];

export const UpcomingEvents = () => {
  const [activeEventId, setActiveEventId] = useState(upcomingEvents[0].id);

  const isActive = (id: number) => activeEventId === id;

  return (
    <section className="py-16 sm:py-24 bg-background" data-nav-theme="dark">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-foreground rounded-full flex-shrink-0" />
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal text-foreground font-general-sans">
              Upcoming Events
            </h2>
          </div>
        </div>

        {/* Outer container */}
        <div
          className="rounded-3xl p-4 sm:p-6"
          style={{
            backdropFilter: "blur(60px) saturate(200%)",
            WebkitBackdropFilter: "blur(60px) saturate(200%)",
            background:
              "linear-gradient(145deg, hsla(0, 0%, 70%, 0.12), hsla(0, 0%, 60%, 0.08), hsla(0, 0%, 50%, 0.06))",
            border: "1px solid hsla(0, 0%, 70%, 0.2)",
            boxShadow:
              "0 12px 48px hsla(0, 0%, 0%, 0.06), inset 0 1px 1px hsla(0, 0%, 100%, 0.15), inset 0 -1px 0 hsla(0, 0%, 0%, 0.03)",
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 items-stretch">
            {/* Left - Image that changes on hover */}
            <div className="liquid-glass rounded-2xl p-3 h-full">
              <div className="relative overflow-hidden rounded-xl shadow-lg w-full aspect-square bg-black/5">
                {allEvents.map((event) => (
                  <img
                    key={event.id}
                    src={event.image}
                    alt={event.title}
                    loading="eager" decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                    style={{ opacity: isActive(event.id) ? 1 : 0 }}
                  />
                ))}
              </div>
            </div>

            {/* Right - Individual bento cards */}
            <div className="flex flex-col gap-3 h-full py-3">
              {upcomingEvents.map((event) => (
                <div
                  key={event.id}
                  onMouseEnter={() => setActiveEventId(event.id)}
                  className={`group liquid-glass rounded-2xl px-5 sm:px-6 py-5 cursor-pointer transition-all duration-300 flex items-center justify-between gap-4 flex-1 ${
                    isActive(event.id)
                      ? "ring-1 ring-[hsla(0,40%,35%,0.25)] bg-[hsla(0,40%,30%,0.06)] shadow-md"
                      : "hover:bg-[hsla(0,0%,0%,0.02)]"
                  }`}
                >
                  <div className="flex-1 space-y-1.5">
                    <div className="text-xs text-muted-foreground/60 font-general-sans tracking-wide uppercase">
                      {event.category}
                    </div>
                    <h3 className="text-lg md:text-xl font-medium text-foreground font-general-sans leading-tight">
                      {event.title}
                    </h3>
                    <div className="text-sm text-muted-foreground/70 font-general-sans font-light">
                      {[event.date, event.time, event.speaker].filter(Boolean).join(" | ")}
                    </div>
                  </div>

                  {event.link?.startsWith("/") ? (
                    <Link
                      to={event.link}
                      aria-label={`${event.ctaLabel || "Register"} for ${event.title}`}
                      className="relative inline-flex items-center justify-center gap-2 rounded-full liquid-glass-burgundy pl-4 pr-1.5 py-1.5 text-xs font-normal text-white shadow-sm transition-all duration-300 font-general-sans overflow-hidden flex-shrink-0 group/btn"
                    >
                      <span className="relative z-10">{event.ctaLabel || "Register"}</span>
                      <span className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover/btn:bg-white/30 transition-colors duration-300">
                        <ArrowRight className="w-3 h-3 -rotate-45 transition-all duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 ease-in-out z-[2]"></span>
                      <span className="absolute inset-0 bg-[hsl(0,0%,10%)] opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 z-[1]"></span>
                    </Link>
                  ) : (
                    <a
                      href={event.link || "#"}
                      target={event.link ? "_blank" : undefined}
                      rel={event.link ? "noopener noreferrer" : undefined}
                      aria-label={event.link ? `${event.ctaLabel || "Register"} for ${event.title}` : `Coming soon: ${event.title}`}
                      className="relative inline-flex items-center justify-center gap-2 rounded-full liquid-glass-burgundy pl-4 pr-1.5 py-1.5 text-xs font-normal text-white shadow-sm transition-all duration-300 font-general-sans overflow-hidden flex-shrink-0 group/btn"
                    >
                      <span className="relative z-10">{event.link ? event.ctaLabel || "Register" : "Coming soon"}</span>
                      <span className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover/btn:bg-white/30 transition-colors duration-300">
                        <ArrowRight className="w-3 h-3 -rotate-45 transition-all duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 ease-in-out z-[2]"></span>
                      <span className="absolute inset-0 bg-[hsl(0,0%,10%)] opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 z-[1]"></span>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Past Events - 3-column bento spanning full width */}
          <div className="mt-4 sm:mt-5 liquid-glass rounded-2xl px-5 sm:px-6 py-4 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs text-muted-foreground/60 font-general-sans tracking-wide uppercase">Past Events</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {pastEvents.map((event) => (
                <div
                  key={event.id}
                  onMouseEnter={() => setActiveEventId(event.id)}
                  className={`liquid-glass rounded-xl px-5 py-4 cursor-pointer transition-all duration-300 ${
                    isActive(event.id) ? "bg-[hsla(0,40%,30%,0.06)] shadow-md ring-1 ring-[hsla(0,40%,35%,0.25)]" : "hover:bg-[hsla(0,0%,0%,0.02)]"
                  }`}
                >
                  <div className="text-[10px] text-muted-foreground/50 font-general-sans tracking-wide uppercase mb-1">
                    {event.category}
                  </div>
                  <h4 className="text-sm font-medium text-foreground font-general-sans leading-snug mb-1.5">
                    {event.title}
                  </h4>
                  <div className="text-xs text-muted-foreground/60 font-general-sans font-light">
                    {event.date} | {event.time}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
